import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FaGraduationCap, FaUsers, FaChartLine,
  FaWater, FaTractor, FaHeartbeat, FaFemale, FaBook
} from 'react-icons/fa';
import ImageSlider from './ImageSlider';
import { GiPlantWatering } from 'react-icons/gi';

const HomePage = () => {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  };

  const cardVariants = {
    initial: { scale: 0.9, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { 
      scale: 1.05,
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100">
      {/* Hero Section */}
      <motion.div 
        className="container mx-auto px-4 pt-24 pb-32 relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-transparent opacity-50" />
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-24">
          <motion.div className="lg:w-[45%] w-full relative z-10" {...fadeIn}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1, delay: 0.5 }}
              className="h-2 bg-[#fbba00] mb-6 rounded-full"
            />
            <h1 className="text-5xl lg:text-6xl font-bold text-[#080366] mb-8 leading-tight">
              Ambuja Foundation
              <span className="block text-[#fbba00] mt-4">Rajasthan</span>
            </h1>
            <p className="text-2xl text-gray-600 mb-10 leading-relaxed">
              Strengthening Livelihood to ensure Financial & 
              Social Security of Local Youths
            </p>
            <div className="flex gap-6">
              <motion.button 
                whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(8,3,102,0.3)" }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/candidate-management')}
                className="px-10 py-4 bg-[#080366] text-white rounded-xl font-semibold text-lg hover:bg-[#060244] transition-all duration-300 shadow-lg"
              >
                View Candidates
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(251,186,0,0.3)" }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/upload-resume')}
                className="px-10 py-4 border-2 border-[#fbba00] text-[#080366] rounded-xl font-semibold text-lg hover:bg-[#fbba00] hover:text-white transition-all duration-300"
              >
                Upload Resume
              </motion.button>
            </div>
          </motion.div>
          
          <motion.div 
  className="lg:w-[50%] w-full"
  initial={{ x: 100, opacity: 0 }}
  animate={{ x: 0, opacity: 1 }}
  transition={{ duration: 1 }}
>
  <div className="relative w-full aspect-[16/8.6] rounded-2xl overflow-hidden shadow-2xl">
    <ImageSlider />
  </div>
</motion.div>
        </div>
      </motion.div>

      {/* Focus Areas Section */}
      <div className="bg-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-50 to-transparent opacity-50" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#080366] mb-4">Our Focus Areas</h2>
            <div className="w-24 h-1 bg-[#fbba00] mx-auto rounded-full" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaWater className="text-5xl" />,
                title: "Water",
                description: "Creating drought-resilient villages and year-round water access for farmers and communities."
              },
              {
                icon: <FaGraduationCap className="text-5xl" />,
                title: "Skill Development",
                description: "Providing training and employment opportunities to lift rural youth and families out of poverty."
              },
              {
                icon: <FaTractor className="text-5xl" />,
                title: "Agriculture",
                description: "Helping farming families thrive by organizing farmers and enhancing their profitability."
              },
              {
                icon: <FaFemale className="text-5xl" />,
                title: "Women Empowerment",
                description: "Empowering women as change-makers, generating income and leading communities forward."
              },
              {
                icon: <FaHeartbeat className="text-5xl" />,
                title: "Health",
                description: "Building healthy communities to enable productive lives and support rural well-being."
              },
              {
                icon: <FaBook className="text-5xl" />,
                title: "Education",
                description: "Ensuring quality education for rural children and helping differently-abled children reach their potential."
              }
            ].map((area, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="initial"
                whileInView="animate"
                whileHover="hover"
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg text-center transform transition-all duration-300 hover:translate-y--2"
              >
                <div className="text-[#fbba00] mb-6 flex justify-center">{area.icon}</div>
                <h3 className="text-2xl font-bold text-[#080366] mb-4">{area.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Impact Section */}
      <div className="bg-[#080366] text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0  opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              {
                icon: <FaGraduationCap className="text-5xl" />,
                stat: "33,000+",
                label: "Youth Trained"
              },
              {
                icon: <FaUsers className="text-5xl" />,
                stat: "25,000+",
                label: "Youth Placed"
              },
              {
                icon: <GiPlantWatering className="text-5xl" />,
                stat: "22.57MCM",
                label: "Water Harvested"
              },
              {
                icon: <FaChartLine className="text-5xl" />,
                stat: "13000+",
                label: "Farmers's Profit Increased"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="text-center p-8 backdrop-blur-sm bg-white/5 rounded-2xl"
              >
                <div className="text-[#fbba00] mb-6 flex justify-center">{item.icon}</div>
                <motion.h3 
                  className="text-4xl font-bold mb-3"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  {item.stat}
                </motion.h3>
                <p className="text-gray-300 text-lg">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* About Us Section */}
      <div className="bg-white py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#080366] mb-4">About Us</h2>
            <div className="w-24 h-1 bg-[#fbba00] mx-auto rounded-full" />
          </motion.div>
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            <motion.div 
              className="md:w-1/2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img 
                  src="images/RajasthanMap.jpg" 
                  alt="Ambuja Foundation Impact" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#080366]/20 to-transparent rounded-2xl" />
              </div>
            </motion.div>
            <motion.div 
              className="md:w-1/2"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-2xl text-gray-600 leading-relaxed text-justify">
                Established in 1993, Ambuja Foundation has had one ambitious goal - tackling rural poverty! 
                We take families and communities on a transformational journey, from a life of poverty to one of prosperity. 
                And whilst the problem is vast, we know we can make a big impact - thanks to the support of our partners. 
                By working together, rural India can thrive, once more.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <motion.div 
        className="bg-gradient-to-r from-[#fbba00] to-[#ffd700] py-24 relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="absolute inset-0  opacity-5" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-[#080366] mb-6">Join Our Initiative</h2>
          <p className="text-2xl text-[#080366] mb-10 max-w-2xl mx-auto">
            Be part of our mission to transform lives through skill development
          </p>
          <motion.button 
            whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(8,3,102,0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/candidate-management')}
            className="px-12 py-4 bg-[#080366] text-white rounded-xl font-semibold text-xl hover:bg-[#060244] transition-all duration-300 shadow-lg"
          >
            Explore Candidates
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage;